import * as React from "react";
import { useNavigate } from "react-router-dom";
import { type ColumnDef } from "@tanstack/react-table";

import { DataTable } from "@/components/ui/data-table";
import type { ProgramApplication } from "@/services/program.service";
import type { StatValue } from "@/types/dashboard";
import { DashboardCard, StatusText } from "@/components/dashboard/dashboard-card";
import { shortDate } from "./helpers";

const columns: ColumnDef<ProgramApplication>[] = [
  {
    accessorKey: "firstName",
    header: "Affiliate",
    meta: { width: "w-44" },
    cell: ({ row }) => (
      <span className="whitespace-nowrap font-medium text-foreground">
        {row.original.firstName} {row.original.lastName}
      </span>
    ),
  },
  {
    accessorKey: "programName",
    header: "Program",
    meta: { width: "w-40" },
    cell: ({ row }) => (
      <span className="truncate text-foreground">{row.original.programName}</span>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    meta: { width: "w-28" },
    cell: ({ row }) => <StatusText status={row.original.status} />,
  },
  {
    accessorKey: "createdAt",
    header: "Applied",
    meta: { width: "w-24" },
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground">
        {shortDate(row.original.createdAt)}
      </span>
    ),
  },
];

const MAX_ROWS = 5;

export function ApplicationsCard({
  data,
  stats,
  isLoading,
}: {
  data: ProgramApplication[] | undefined;
  stats?: StatValue[];
  isLoading: boolean;
}) {
  const navigate = useNavigate();
  const rows = React.useMemo(
    () =>
      [...(data ?? [])]
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        .slice(0, MAX_ROWS),
    [data]
  );

  return (
    <DashboardCard
      id="dashboard-applications-card"
      title="Applications"
      to="/brand/affiliates/applicants"
      stats={stats}
      bodyClassName="px-3 pb-3"
    >
      <DataTable
        columns={columns}
        data={rows}
        isLoading={isLoading}
        skeletonRows={3}
        getRowId={(row) => row.applicationId}
        onRowClick={(row) => navigate(`/brand/affiliates/${row.id}`)}
        emptyText="No applications yet"
        minTableWidth="min-w-[28rem]"
      />
    </DashboardCard>
  );
}
